import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import { useAuth } from "@/context/AuthContext";
import { trackProductEvent } from "@/lib/productAnalytics";
import { desktopNotificationState, enableDesktopNotifications } from "@/lib/pushNotifications";
import Nav from "@/components/Nav";
import WorkingHourglass from "@/components/WorkingHourglass";
import {
  Check,
  CheckCircle,
  Clock,
  Gauge,
  Info,
  UploadSimple,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function defaultCallDateTime() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function callDateTimeInputValue(value) {
  if (!value) return defaultCallDateTime();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return defaultCallDateTime();
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function formatElapsed(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${String(remaining).padStart(2, "0")}`;
}

const PROCESSING_STEPS = [
  {
    label: "Transcribing audio",
    detail: "Turning the recording into a structured conversation with speakers and timing.",
  },
  {
    label: "Dissecting conversation",
    detail: "Connecting signals, context, coaching, and relationship memory.",
  },
];

const SUPPORTED_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/aac",
  "audio/mp4",
  "audio/m4a",
  "audio/x-m4a",
];

export default function UploadPage() {
  const { legalVersion } = useAuth();
  const location = useLocation();
  const uploadQuery = new URLSearchParams(location.search);
  const queryCustomer = (uploadQuery.get("customerName") || "").trim();
  const prefilledRelationshipId = (uploadQuery.get("relationshipId") || "").trim();
  const prefilledCustomer = typeof location.state?.customerName === "string"
    ? location.state.customerName.trim()
    : queryCustomer;
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState(() => prefilledCustomer);
  const [occurredAt, setOccurredAt] = useState(defaultCallDateTime);
  const [recordingAuthority, setRecordingAuthority] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [progressStep, setProgressStep] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [betaLimits, setBetaLimits] = useState(null);
  const [pageError, setPageError] = useState("");
  const [existingCallCount, setExistingCallCount] = useState(null);
  const [uploadInFlight, setUploadInFlight] = useState(false);
  const [activeCallId, setActiveCallId] = useState(null);
  const [processingStartedAt, setProcessingStartedAt] = useState(null);
  const [processingFilename, setProcessingFilename] = useState("");
  const [notificationState, setNotificationState] = useState("unknown");
  const [speakerConfirmation, setSpeakerConfirmation] = useState(null);
  const [speakerRoles, setSpeakerRoles] = useState({});
  const [speakerNames, setSpeakerNames] = useState({});
  const [speakerSubmitting, setSpeakerSubmitting] = useState(false);
  const processingCompletionHandledRef = useRef(false);
  const speakerConfirmationTrackedRef = useRef(false);
  const inputRef = useRef();
  const nav = useNavigate();

  const cleanTitle = title.trim();
  const cleanCustomer = customer.trim();
  const quotaExhausted = Boolean(
    betaLimits?.quota_enforced && betaLimits?.uploaded_calls_remaining === 0,
  );
  const maxUploadMb = betaLimits?.max_upload_mb || 100;

  const applyProcessingState = (state) => {
    if (!state) return;
    setActiveCallId(state.call_id || null);
    setProcessingStartedAt(state.started_at || null);
    if (state.filename) setProcessingFilename(state.filename);
    if (state.occurred_at) setOccurredAt(callDateTimeInputValue(state.occurred_at));
    const awaitingSpeakerConfirmation = state.status === "awaiting_speaker_confirmation";
    const isActive = ["queued", "transcribing", "awaiting_speaker_confirmation", "analyzing"].includes(state.status);
    if (isActive) setRecordingAuthority(true);
    setBusy(isActive);
    setSpeakerConfirmation(awaitingSpeakerConfirmation ? state.speaker_confirmation || null : null);
    if (awaitingSpeakerConfirmation) {
      setProgressStep(1);
      setProgress("Confirm who is speaking");
    } else if (state.status === "analyzing" || (state.status === "queued" && Number(state.stage) >= 2)) {
      setProgressStep(2);
      setProgress(state.status === "analyzing" ? "Dissecting conversation..." : "Preparing Dissection...");
    } else if (["queued", "transcribing"].includes(state.status)) {
      setProgressStep(1);
      setProgress("Transcribing audio...");
    }
  };

  const canSubmit =
    Boolean(file)
    && Boolean(cleanTitle)
    && Boolean(cleanCustomer)
    && Boolean(occurredAt)
    && recordingAuthority
    && !quotaExhausted
    && !busy;

  const readiness = useMemo(() => [
    {
      label: "Audio selected",
      ready: Boolean(file || processingFilename),
      detail: file
        ? `${(file.size / 1024 / 1024).toFixed(2)} MB · ${file.name}`
        : processingFilename ? `${processingFilename} · upload received` : "Choose an MP3, WAV, AAC, or M4A file.",
    },
    {
      label: "Conversation context",
      ready: Boolean(cleanTitle && cleanCustomer && occurredAt),
      detail: cleanTitle && cleanCustomer ? `${cleanCustomer} · ${cleanTitle}` : "Add a title, customer, and call date.",
    },
    {
      label: "Permission confirmed",
      ready: recordingAuthority,
      detail: recordingAuthority ? "Recording authority confirmed." : "Confirm that you are authorised to process the recording.",
    },
  ], [cleanCustomer, cleanTitle, file, occurredAt, processingFilename, recordingAuthority]);

  const submitLabel = useMemo(() => {
    if (busy) return progress || "Working...";
    if (quotaExhausted) return "Call allowance reached";
    if (!file) return "Choose audio to continue";
    if (!cleanTitle || !cleanCustomer || !occurredAt) return "Complete call details";
    if (!recordingAuthority) return "Confirm permission to continue";
    return "Dissect call";
  }, [busy, cleanCustomer, cleanTitle, file, occurredAt, progress, quotaExhausted, recordingAuthority]);

  useEffect(() => {
    if (!prefilledRelationshipId) return undefined;
    let active = true;
    api.get(`/customers/${encodeURIComponent(prefilledRelationshipId)}/timeline`)
      .then(({ data }) => {
        const canonicalName = String(data?.customer?.name || "").trim();
        if (!active || !canonicalName) return;
        setCustomer((current) => {
          const normalizedCurrent = String(current || "").trim();
          if (!normalizedCurrent || normalizedCurrent === prefilledCustomer) return canonicalName;
          return current;
        });
      })
      .catch(() => {
        // The URL also carries the visible customer name, so a timeline lookup
        // failure must never block or clear the prefill.
      });
    return () => {
      active = false;
    };
  }, [prefilledCustomer, prefilledRelationshipId]);

  useEffect(() => {
    let active = true;

    api.get("/calls")
      .then(({ data }) => {
        if (!active || !Array.isArray(data)) return;
        setExistingCallCount(data.length);
        trackProductEvent("upload_page_viewed", { call_count: data.length, section: data.length ? "repeat_call" : "first_call" });
        setTitleSuggestions(uniqueValues(data.map((call) => call.title)));
        setCustomerSuggestions(
          uniqueValues(
            data
              .filter((call) => call.is_customer_conversation !== false)
              .flatMap((call) => [call.customer_name, call.company]),
          ),
        );
      })
      .catch(() => {
        // Suggestions are optional; uploading remains available.
      });

    api.get("/beta/limits")
      .then(({ data }) => {
        if (active) setBetaLimits(data);
      })
      .catch(() => {
        // The backend remains authoritative if allowance visibility cannot load.
      });

    api.get("/calls/processing/current")
      .then(({ data }) => {
        if (!active || !data?.call_id) return;
        processingCompletionHandledRef.current = false;
        setTitle(data.title || "");
        setCustomer(data.customer_name || "");
        applyProcessingState(data);
        desktopNotificationState().then((state) => {
          if (active) setNotificationState(state);
        });
      })
      .catch(() => {
        // Recovery visibility is helpful but must never block a new upload.
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!busy) {
      setElapsedSeconds(0);
      return undefined;
    }
    const parsedStartedAt = processingStartedAt ? new Date(processingStartedAt).getTime() : Date.now();
    const startedAt = Number.isFinite(parsedStartedAt) ? parsedStartedAt : Date.now();
    const update = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [busy, processingStartedAt]);

  useEffect(() => {
    if (!uploadInFlight) return undefined;
    const warnBeforeLeave = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeave);
    return () => window.removeEventListener("beforeunload", warnBeforeLeave);
  }, [uploadInFlight]);

  useEffect(() => {
    const speakers = Array.isArray(speakerConfirmation?.speakers)
      ? speakerConfirmation.speakers.map((item) => item?.speaker).filter(Boolean)
      : [];
    if (!speakers.length) {
      setSpeakerRoles({});
      setSpeakerNames({});
      speakerConfirmationTrackedRef.current = false;
      return;
    }
    setSpeakerRoles((current) => Object.fromEntries(
      speakers.map((speaker) => [speaker, ["you", "client", "other"].includes(current[speaker]) ? current[speaker] : ""]),
    ));
    setSpeakerNames((current) => Object.fromEntries(
      speakers.map((speaker) => [speaker, current[speaker] || ""]),
    ));
    if (!speakerConfirmationTrackedRef.current) {
      speakerConfirmationTrackedRef.current = true;
      trackProductEvent("audio_speaker_confirmation_required", {
        section: "audio_upload",
        outcome: "waiting_for_user",
      });
    }
  }, [speakerConfirmation]);

  useEffect(() => {
    if (!activeCallId) return undefined;
    let cancelled = false;
    let timer = 0;

    const poll = async () => {
      try {
        const { data } = await api.get(`/calls/${activeCallId}/processing`);
        if (cancelled || !data) return;
        applyProcessingState(data);

        if (data.status === "ready" && !processingCompletionHandledRef.current) {
          processingCompletionHandledRef.current = true;
          setBusy(true);
          setProgressStep(3);
          setProgress(data.outside_customer_workflow ? "Content reviewed" : "Dissection complete");
          if (data.outside_customer_workflow) toast.info("This recording was analysed outside the customer workflow.");
          else toast.success("Your call is ready");
          trackProductEvent(data.first_result ? "first_upload_completed" : "repeat_upload_completed", {
            call_count: data.call_count,
            section: "analysis_ready",
            outcome: data.outside_customer_workflow ? "outside_customer_workflow" : "success",
          });
          timer = window.setTimeout(() => {
            nav(`/calls/${activeCallId}`, {
              state: { from: "upload", firstResult: Boolean(data.first_result), callCount: data.call_count },
            });
          }, 900);
          return;
        }

        if (data.status === "partial" && !processingCompletionHandledRef.current) {
          processingCompletionHandledRef.current = true;
          setBusy(false);
          toast.warning(data.error || "The transcript is saved. Open the call to retry the dissection.");
          trackProductEvent(data.first_result ? "first_upload_completed" : "repeat_upload_completed", {
            call_count: data.call_count, section: "transcript_only", outcome: "partial_success",
          });
          nav(`/calls/${activeCallId}`, {
            state: { from: "upload", firstResult: Boolean(data.first_result), callCount: data.call_count },
          });
          return;
        }

        if (data.status === "failed" && !processingCompletionHandledRef.current) {
          processingCompletionHandledRef.current = true;
          setBusy(false);
          setActiveCallId(null);
          setProcessingStartedAt(null);
          setProcessingFilename("");
          const message = data.error || "The call could not be processed. Please try the upload again.";
          setPageError(message);
          toast.error(message);
          return;
        }

        timer = window.setTimeout(poll, Number(data.poll_after_ms) || 2000);
      } catch {
        if (!cancelled) timer = window.setTimeout(poll, 3000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [activeCallId, nav]);

  const enableNotifications = async () => {
    setNotificationState("enabling");
    try {
      const state = await enableDesktopNotifications();
      if (state === "default") {
        setNotificationState("permission-help");
        toast.info("Your browser did not show the notification permission prompt.");
        return;
      }
      setNotificationState(state);
      if (state === "enabled") toast.success("Desktop notification enabled");
      else if (state === "denied") toast.info("Desktop notifications remain off");
    } catch {
      setNotificationState("unavailable");
      toast.warning("Desktop notifications could not be enabled on this browser.");
    }
  };

  const showError = (message) => {
    setPageError(message);
    toast.error(message);
  };

  const onPick = (pickedFile) => {
    if (!pickedFile || busy) return;
    setPageError("");

    if (quotaExhausted) {
      showError("Your call allowance has been used. New uploads are disabled.");
      return;
    }
    if (pickedFile.size > maxUploadMb * 1024 * 1024) {
      showError(`This file exceeds the ${maxUploadMb} MB beta limit. Choose a smaller recording.`);
      return;
    }
    if (
      !SUPPORTED_AUDIO_TYPES.includes(pickedFile.type)
      && !/\.(mp3|wav|aac|m4a)$/i.test(pickedFile.name)
    ) {
      showError("Callsect could not recognise this audio. Choose an MP3, WAV, AAC, or M4A file.");
      return;
    }

    setFile(pickedFile);
    if (!title.trim()) setTitle(pickedFile.name.replace(/\.[^.]+$/, ""));
  };

  const clearFile = () => {
    if (busy) return;
    setFile(null);
    setPageError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const openFilePicker = () => {
    if (quotaExhausted) {
      showError("Your call allowance has been used. New uploads are disabled.");
      return;
    }
    if (!busy) inputRef.current?.click();
  };

  const confirmSpeakers = async () => {
    if (!activeCallId || speakerSubmitting) return;
    const detectedSpeakers = Array.isArray(speakerConfirmation?.speakers)
      ? speakerConfirmation.speakers.map((item) => item?.speaker).filter(Boolean)
      : [];
    const youSpeakers = detectedSpeakers.filter((speaker) => speakerRoles[speaker] === "you");
    const clientSpeakers = detectedSpeakers.filter((speaker) => speakerRoles[speaker] === "client");
    const unassignedSpeakers = detectedSpeakers.filter((speaker) => !speakerRoles[speaker]);
    if (unassignedSpeakers.length) {
      showError("Label every detected voice as YOU, CLIENT, or OTHER.");
      return;
    }
    if (youSpeakers.length !== 1) {
      showError("Choose exactly one detected voice as YOU.");
      return;
    }
    if (!clientSpeakers.length) {
      showError("Choose at least one detected voice as CLIENT.");
      return;
    }
    setSpeakerSubmitting(true);
    setPageError("");
    try {
      const { data } = await api.post(`/calls/${activeCallId}/speaker-confirmation`, {
        rep_speaker: youSpeakers[0],
        speaker_roles: speakerRoles,
        speaker_names: speakerNames,
      });
      speakerConfirmationTrackedRef.current = false;
      applyProcessingState(data);
      setProgressStep(2);
      setProgress("Dissecting conversation...");
      toast.success("Speakers confirmed. Dissection started.");
      trackProductEvent("audio_speaker_confirmation_completed", {
        section: "audio_upload",
        outcome: "confirmed",
      });
    } catch (error) {
      const message = getActionableError(error, "Callsect could not confirm these speakers.");
      setPageError(message);
      toast.error(message);
    } finally {
      setSpeakerSubmitting(false);
    }
  };

  const removeWaitingUpload = async () => {
    if (!activeCallId || speakerSubmitting) return;
    if (!window.confirm("Remove this upload? No Dissection has been generated.")) return;
    setSpeakerSubmitting(true);
    setPageError("");
    try {
      await api.delete(`/calls/${activeCallId}`);
      setBusy(false);
      setActiveCallId(null);
      setProcessingStartedAt(null);
      setProcessingFilename("");
      setSpeakerConfirmation(null);
      setSpeakerRoles({});
      setSpeakerNames({});
      setProgress("");
      setProgressStep(0);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      toast.info("Upload removed");
    } catch (error) {
      const message = getActionableError(error, "This upload could not be removed.");
      setPageError(message);
      toast.error(message);
    } finally {
      setSpeakerSubmitting(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setValidationAttempted(true);
    setPageError("");

    if (quotaExhausted) return showError("Your call allowance has been used. New uploads are disabled.");
    if (!file) return showError("Choose an audio file first.");
    if (!cleanTitle) return showError("Add a clear call title.");
    if (!cleanCustomer) return showError("Add a customer or company name.");
    if (!recordingAuthority) {
      return showError("Confirm that you are authorised to process the recording.");
    }

    const callDate = new Date(occurredAt);
    if (!occurredAt || Number.isNaN(callDate.getTime())) {
      return showError("Choose a valid call date and time.");
    }

    setBusy(true);
    setUploadInFlight(true);
    setProcessingStartedAt(new Date().toISOString());
    setProgressStep(1);
    setProgress("Uploading audio...");
    processingCompletionHandledRef.current = false;
    trackProductEvent("upload_started", {
      call_count: Number.isFinite(existingCallCount) ? existingCallCount : undefined,
      section: existingCallCount === 0 ? "first_call" : existingCallCount > 0 ? "repeat_call" : "unknown",
    });

    const formData = new FormData();
    formData.append("audio", file);
    formData.append("title", cleanTitle);
    formData.append("customer_name", cleanCustomer);
    formData.append("occurred_at", callDate.toISOString());
    formData.append("recording_authority_confirmed", String(recordingAuthority));
    formData.append("legal_version", legalVersion);

    try {
      const { data } = await api.post("/calls/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 1800000,
      });
      setUploadInFlight(false);
      const processing = data?.processing || {
        call_id: data?.id,
        status: "queued",
        stage: 1,
        started_at: new Date().toISOString(),
      };
      applyProcessingState(processing);
      setProgress("Transcribing audio...");
      setProgressStep(1);
      const state = await desktopNotificationState();
      setNotificationState(state);
    } catch (error) {
      setUploadInFlight(false);
      setBusy(false);
      setActiveCallId(null);
      setProcessingStartedAt(null);
      setProgressStep(0);
      setProgress("");
      const message = getActionableError(error, "The upload could not be completed. Try again.");
      trackProductEvent("upload_failed", {
        call_count: Number.isFinite(existingCallCount) ? existingCallCount : undefined,
        section: existingCallCount === 0 ? "first_call" : existingCallCount > 0 ? "repeat_call" : "unknown",
        outcome: error?.response?.status ? `http_${error.response.status}` : "network_or_unknown",
      });
      setPageError(message);
      toast.error(message);
    }
  };

  return (
    <div className="upload-page min-h-screen">
      <Nav />

      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-14 product-page-enter">
                <header className="upload-hero mb-8 sm:mb-10">
          <div>
            <div className="brand-eyebrow">NEW CALL</div>
            <h1 className="mt-4 text-4xl font-semibold tracking-[-0.045em] text-white sm:text-5xl">
              Add a new call
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-400 sm:text-base">
              Upload a recording and add the conversation details.
            </p>
          </div>

          <div className="upload-hero__flow" aria-label="Upload flow">
            <span className="is-active"><strong>1</strong> Add audio</span>
            <span><strong>2</strong> Confirm context</span>
            <span><strong>3</strong> Review results</span>
          </div>
        </header>

        <section className="upload-transcript-shortcut" aria-label="Transcript import alternative">
          <span>Already have a transcript?</span>
          <Link to="/import-transcript">Import transcript →</Link>
        </section>

        <form onSubmit={submit} className="upload-form surface-card overflow-visible p-4 sm:p-6 lg:p-7">
            {pageError && (
              <div className="upload-error-banner" role="alert">
                <WarningCircle size={20} weight="duotone" />
                <div>
                  <strong>This upload needs attention</strong>
                  <span>{pageError}</span>
                </div>
                <button type="button" onClick={() => setPageError("")} aria-label="Dismiss upload error">
                  <X size={16} weight="bold" />
                </button>
              </div>
            )}

            <div
              className={`upload-dropzone ${dragActive ? "is-dragging" : ""} ${file ? "has-file" : ""} ${busy ? "is-disabled" : ""}`}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragEnter={(event) => {
                event.preventDefault();
                if (!busy) setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                if (!busy) setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDragActive(false);
                onPick(event.dataTransfer.files?.[0]);
              }}
              role="button"
              tabIndex={busy ? -1 : 0}
              aria-disabled={busy}
              data-testid="dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept=".mp3,.wav,.aac,.m4a,audio/*"
                className="hidden"
                onChange={(event) => onPick(event.target.files?.[0])}
                disabled={busy}
                data-testid="file-input"
              />


              {(file || (busy && processingFilename)) ? (
                <div className="relative z-10 flex w-full items-center gap-4 text-left">
                  <div className="upload-file-mark" aria-hidden="true">
                    <span /><span /><span /><span />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-medium text-white">{file?.name || processingFilename}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      {file && <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>}
                      {file && <span className="hidden h-1 w-1 rounded-full bg-slate-700 sm:block" />}
                      <span className="text-emerald-300">{busy ? "Upload received" : "Audio ready"}</span>
                    </div>
                  </div>

                  {file && !busy && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        clearFile();
                      }}
                      className="upload-clear-button"
                      aria-label="Remove selected audio file"
                      data-testid="clear-file-btn"
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="relative z-10 flex flex-col items-center">
                  <div className="upload-icon-shell" aria-hidden="true">
                    <UploadSimple size={30} weight="light" />
                  </div>
                  <div className="mt-5 text-base font-medium text-white sm:text-lg">
                    Drop audio here or click to browse
                  </div>
                  <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                    mp3 · wav · aac · m4a · max {maxUploadMb}MB
                  </div>
                </div>
              )}
            </div>

            {betaLimits?.quota_enforced ? <BetaAllowanceMeta limits={betaLimits} /> : null}

            <div className="my-6 segmented-divider" />

            <fieldset disabled={busy} className="space-y-5 disabled:opacity-65">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Field
                  label="Call title"
                  value={title}
                  setValue={setTitle}
                  testId="title-input"
                  placeholder="Renewal call — Q3"
                  suggestions={titleSuggestions}
                  autoFocus={Boolean(prefilledCustomer)}
                  required
                  showError={validationAttempted}
                  errorMessage="Call title is required."
                />
                <Field
                  label="Customer"
                  hint="person or company"
                  value={customer}
                  setValue={setCustomer}
                  testId="customer-input"
                  placeholder="John Griffin or Acme Corp"
                  suggestions={customerSuggestions}
                  required
                  showError={validationAttempted}
                  errorMessage="Customer or company name is required."
                />
              </div>

              <div>
                <label className="upload-field-label" htmlFor="occurred-at">Call date and time</label>
                <input
                  id="occurred-at"
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                  data-testid="occurred-at-input"
                  className="upload-input mt-2"
                />
                <div className="mt-2 text-xs leading-5 text-slate-600">
                  This places the call correctly in the customer&apos;s relationship timeline.
                </div>
              </div>

              <section className="upload-readiness" aria-label="Upload readiness">
                <div className="upload-readiness__header">
                  <div>
                    <span>Preflight check</span>
                    <strong>{readiness.filter((item) => item.ready).length} of {readiness.length} ready</strong>
                  </div>
                  <span className={canSubmit ? "is-ready" : ""}>
                    {canSubmit ? "Ready to process" : "Complete the remaining items"}
                  </span>
                </div>
                <div className="upload-readiness__list">
                  {readiness.map((item) => (
                    <article key={item.label} className={item.ready ? "is-ready" : ""}>
                      <span className="upload-readiness__marker" aria-hidden="true">
                        {item.ready ? <Check size={14} weight="bold" /> : <span />}
                      </span>
                      <div>
                        <strong>{item.label}</strong>
                        <small>{item.detail}</small>
                      </div>
                    </article>
                  ))}
                </div>
              </section>

              <label className="upload-consent">
                <input
                  type="checkbox"
                  checked={recordingAuthority}
                  onChange={(event) => setRecordingAuthority(event.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-violet-500"
                  data-testid="recording-authority-checkbox"
                />
                <span>
                  I confirm that I am authorised to upload and process this recording and have provided any notice or obtained any consent required by applicable law. See the{" "}
                  <Link to="/privacy" target="_blank" className="text-violet-300 transition hover:text-violet-200">Privacy Notice</Link>{" "}
                  and{" "}
                  <Link to="/data-handling" target="_blank" className="text-violet-300 transition hover:text-violet-200">data-handling summary</Link>.
                </span>
              </label>
            </fieldset>

            {!speakerConfirmation && (
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="upload-submit-btn"
                className="upload-submit-button upload-submit-button--compact-gate mt-6"
              >
                {busy && <WorkingHourglass size={15} />}
                {submitLabel}
              </button>
            )}

            {!busy && (
              <p className="upload-submit-note">
                <Clock size={14} weight="duotone" />
                Longer recordings can take several minutes. Callsect will keep this page updated throughout processing.
              </p>
            )}

            {speakerConfirmation ? (
              <SpeakerConfirmationPanel
                confirmation={speakerConfirmation}
                speakerRoles={speakerRoles}
                setSpeakerRoles={setSpeakerRoles}
                speakerNames={speakerNames}
                setSpeakerNames={setSpeakerNames}
                submitting={speakerSubmitting}
                onConfirm={confirmSpeakers}
                onRemove={removeWaitingUpload}
              />
            ) : busy && (
              <ProcessingPanel
                progressStep={progressStep}
                progress={progress}
                elapsedSeconds={elapsedSeconds}
                uploadInFlight={uploadInFlight}
                notificationState={notificationState}
                onEnableNotifications={enableNotifications}
              />
            )}
        </form>
      </main>
    </div>
  );
}

function BetaAllowanceMeta({ limits }) {
  const remaining = limits.uploaded_calls_remaining;
  const used = limits.uploaded_calls_used || 0;
  const total = limits.uploaded_calls_limit;
  const level = limits.uploaded_calls_warning?.level || "normal";
  const exhausted = level === "exhausted" || remaining === 0;
  const near = level === "near" || remaining === 1;

  return (
    <div className={`upload-allowance-meta ${exhausted ? "is-exhausted" : near ? "is-near" : ""}`} aria-live="polite">
      <Gauge size={14} weight="duotone" aria-hidden="true" />
      <span>
        {exhausted
          ? "Call allowance used · New uploads are disabled"
          : `${remaining} ${remaining === 1 ? "call" : "calls"} remaining · ${used}/${total} used · Up to ${limits.max_audio_minutes} min · ${limits.max_upload_mb} MB`}
      </span>
    </div>
  );
}

function SpeakerConfirmationPanel({
  confirmation,
  speakerRoles,
  setSpeakerRoles,
  speakerNames,
  setSpeakerNames,
  submitting,
  onConfirm,
  onRemove,
}) {
  const speakers = Array.isArray(confirmation?.speakers) ? confirmation.speakers : [];
  const roleOptions = [
    { value: "you", label: "YOU" },
    { value: "client", label: "CLIENT" },
    { value: "other", label: "OTHER" },
  ];
  const roleHelp = {
    you: "Your voice",
    client: "Customer participant",
    other: "Excluded from Dissection",
  };
  const allAssigned = speakers.length > 0 && speakers.every((item) => speakerRoles[item.speaker]);
  const youCount = speakers.filter((item) => speakerRoles[item.speaker] === "you").length;
  const clientCount = speakers.filter((item) => speakerRoles[item.speaker] === "client").length;
  const mappingReady = allAssigned && youCount === 1 && clientCount >= 1;

  return (
    <section className="speaker-confirmation mt-5" aria-labelledby="speaker-confirmation-title">
      <div className="speaker-confirmation__header">
        <div>
          <span>Speaker check</span>
          <h2 id="speaker-confirmation-title">Who is speaking?</h2>
          <p>
            {confirmation?.message || "Label each detected voice before Callsect dissects the conversation."}
          </p>
        </div>
        <strong>{speakers.length} voices detected</strong>
      </div>

      <div className="speaker-confirmation__list">
        {speakers.map((item) => {
          const source = item.speaker;
          const role = speakerRoles[source] || "";
          return (
            <article key={source} className={role ? `is-selected is-${role}` : ""}>
              <div className="speaker-confirmation__choice">
                <span>
                  <strong>{source}</strong>
                  <small>{role ? roleHelp[role] : "Choose a role"}</small>
                </span>
              </div>

              <div className="speaker-confirmation__roles" role="group" aria-label={`Role for ${source}`}>
                {roleOptions.map((option) => (
                  <button
                    type="button"
                    key={option.value}
                    className={role === option.value ? "is-active" : ""}
                    aria-pressed={role === option.value}
                    onClick={() => setSpeakerRoles((current) => ({ ...current, [source]: option.value }))}
                    disabled={submitting}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="speaker-confirmation__excerpts">
                {(item.excerpts || []).map((excerpt, index) => (
                  <blockquote key={`${source}-${index}`}>“{excerpt}”</blockquote>
                ))}
              </div>

              {role === "client" && (
                <label className="speaker-confirmation__name">
                  <span>Name <small>optional</small></span>
                  <input
                    type="text"
                    maxLength={80}
                    value={speakerNames[source] || ""}
                    onChange={(event) => setSpeakerNames((current) => ({
                      ...current,
                      [source]: event.target.value,
                    }))}
                    placeholder="e.g. Sarah Chen"
                    disabled={submitting}
                  />
                </label>
              )}
            </article>
          );
        })}
      </div>

      <div className="speaker-confirmation__actions">
        <button
          type="button"
          className="upload-submit-button upload-submit-button--compact-gate"
          onClick={onConfirm}
          disabled={!mappingReady || submitting}
          data-testid="confirm-audio-speakers"
        >
          {submitting && <WorkingHourglass size={15} />}
          {submitting ? "Confirming speakers..." : "Confirm speakers and start Dissection"}
        </button>
        <button type="button" onClick={onRemove} disabled={submitting}>
          Remove upload
        </button>
      </div>
      <p className="speaker-confirmation__note">
        YOU and CLIENT keep their original timestamps for Dissection. OTHER voices are excluded from analysis. Relationship Memory is not updated until you confirm this mapping.
      </p>
    </section>
  );
}

function ProcessingPanel({ progressStep, progress, elapsedSeconds, uploadInFlight, notificationState, onEnableNotifications }) {
  const complete = progressStep === 3;

  return (
    <section
      className={`processing-panel mt-5 ${complete ? "is-complete" : ""}`}
      role="status"
      aria-live="polite"
      data-testid={complete ? "dissection-complete" : "dissection-progress"}
    >
      <div className="processing-panel__visual" aria-hidden="true">
        {complete ? (
          <CheckCircle size={44} weight="fill" />
        ) : (
          <div className={`dissection-lines ${progressStep === 2 ? "is-dissecting" : "is-transcribing"}`}>
            <span /><span /><span /><span /><span />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="processing-panel__title text-sm font-semibold text-white">
              {!complete && <WorkingHourglass size={15} />}
              <span>{complete ? "Dissection complete" : progress}</span>
            </div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              {complete
                ? "Opening the call with your results ready..."
                : uploadInFlight
                  ? "Keep this window open until the audio upload is complete."
                  : "This can take up to a minute. You can safely leave Callsect while it finishes."}
            </div>
          </div>
          <div className="processing-panel__meta">
            {!complete && <span>Stage {Math.min(progressStep, 2)} of 2</span>}
            <span><Clock size={13} /> {formatElapsed(elapsedSeconds)}</span>
          </div>
        </div>

        {!complete && (
          <div className="mt-4 space-y-3">
            {PROCESSING_STEPS.map((step, index) => {
              const stepNumber = index + 1;
              const isComplete = progressStep > stepNumber;
              const isActive = progressStep === stepNumber;

              return (
                <div
                  key={step.label}
                  className={`processing-step ${isComplete ? "is-complete" : ""} ${isActive ? "is-active" : ""}`}
                >
                  <div className="processing-step__marker">{isComplete ? "✓" : stepNumber}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{step.label}</div>
                    <div className="mt-0.5 text-xs text-slate-600">{step.detail}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!complete && !uploadInFlight && ["default", "enabling"].includes(notificationState) && (
          <div className="processing-panel__notification">
            <div>
              <strong>Want a desktop alert when this call is ready?</strong>
              <span>You can close Callsect after the upload. Processing will continue in the background.</span>
            </div>
            <button type="button" onClick={onEnableNotifications} disabled={notificationState === "enabling"}>
              {notificationState === "enabling" ? "Enabling..." : "Enable notifications"}
            </button>
          </div>
        )}

        {!complete && !uploadInFlight && notificationState === "permission-help" && (
          <div className="processing-panel__notification">
            <div>
              <strong>Your browser kept notifications on Ask.</strong>
              <span>Use the site controls beside the address bar to allow notifications for callsect.com, then retry.</span>
            </div>
            <button type="button" onClick={onEnableNotifications}>Retry</button>
          </div>
        )}

        {!complete && !uploadInFlight && notificationState === "enabled" && (
          <div className="processing-panel__notification is-enabled">
            <CheckCircle size={16} weight="fill" />
            <span>Desktop alert enabled. You can leave Callsect and we will let you know when this is ready.</span>
          </div>
        )}

        {!complete && elapsedSeconds >= 90 && (
          <div className="processing-panel__patience-note">
            <Info size={15} weight="duotone" />
            Longer calls can take more time. Processing is still active; do not upload the same file again.
          </div>
        )}
      </div>
    </section>
  );
}


function uniqueValues(values) {
  const unique = new Map();

  values.forEach((value) => {
    const clean = typeof value === "string" ? value.trim() : "";
    if (!clean) return;
    const key = clean.toLocaleLowerCase();
    if (!unique.has(key)) unique.set(key, clean);
  });

  return [...unique.values()].sort((a, b) => a.localeCompare(b));
}

function Field({
  label,
  hint,
  value,
  setValue,
  testId,
  placeholder,
  suggestions = [],
  autoFocus = false,
  required = false,
  showError = false,
  errorMessage,
}) {
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const query = value.trim().toLocaleLowerCase();
  const inputId = `${testId}-field`;
  const errorId = `${testId}-error`;
  const hasError = required && !query && (touched || showError);
  const matches = query.length >= 2
    ? suggestions
        .filter((suggestion) => {
          const normalized = suggestion.toLocaleLowerCase();
          const beginsWithQuery = normalized.split(/\s+/).some((word) => word.startsWith(query));
          return beginsWithQuery && normalized !== query;
        })
        .slice(0, 6)
    : [];

  return (
    <div className="relative">
      <label className="upload-field-label" htmlFor={inputId}>
        {label}
        {required && (
          <>
            <span className="upload-field-required" aria-hidden="true">*</span>
            <span className="sr-only"> required</span>
          </>
        )}
        {hint && <span className="ml-2 font-normal normal-case tracking-normal text-slate-600">({hint})</span>}
      </label>
      <input
        id={inputId}
        data-testid={testId}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setTouched(true);
          window.setTimeout(() => setFocused(false), 120);
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={focused && matches.length > 0}
        aria-required={required}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        required={required}
        className={`upload-input mt-2${hasError ? " is-invalid" : ""}`}
      />
      {hasError && (
        <div id={errorId} className="upload-field-error" role="alert">
          {errorMessage || `${label} is required.`}
        </div>
      )}
      {focused && matches.length > 0 && (
        <div role="listbox" className="upload-suggestions" data-testid={`${testId}-suggestions`}>
          {matches.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              role="option"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setValue(suggestion);
                setFocused(false);
              }}
              className="upload-suggestion"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
